import { hapticFeedback } from '../lib/haptics.js';
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FormEvent, PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import type {
  AIModel,
  ChatOverview,
  ChatPrefPatch,
  ChatSearchResult,
  ChatUser,
  DirectMessage,
  GroupChat,
} from '@volt/shared';
import type { ApiClient } from '../api/client.js';
import { ApiError } from '../api/client.js';
import { useI18n } from '../i18n/context.js';
import { useFocusTrap } from '../hooks/useFocusTrap.js';
import { lockBodyScroll } from '../lib/bodyScrollLock.js';
import {
  disableChatPush,
  enableChatPush,
  getChatPushState,
  type ChatPushState,
} from '../lib/chatPush.js';
import { AUTO_MODEL_CHOICE_ID } from '../lib/modelPicker.js';
import { Icon } from './Icon.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { ModelPicker } from './ModelPicker.js';
import { renderChatList } from './DirectChatList.js';
import { ChatAttachments } from './DirectChatAttachments.js';
import { IconButton } from './IconButton.js';
import { VoiceRecorderButton } from './VoiceRecorderButton.js';
import {
  FILE_INPUT_ACCEPT,
  PHOTO_INPUT_ACCEPT,
  dataUrlToBase64,
  imageFileToDataUrl,
  readFileAsDataUrl,
} from '../lib/composerAttachments.js';
import {
  aliasMap,
  buildChatList,
  friendLabel,
  friendStrip,
  visibleChatList,
} from './directChatList.js';
import {
  canEditDirectMessage,
  compareDirectMessages,
  groupDirectMessageBatches,
  isAiDirectMessage,
  orderedForwardIds,
  readDirectChatPreferences,
  resolveDirectChatModelId,
  toggleDirectMessageSelection,
  writeDirectChatPreferences,
} from './directChatPreferences.js';

interface DirectChatScreenProps {
  api: ApiClient;
  publicId: number;
  userId: string;
  username: string | null;
  onClose: () => void;
  models?: AIModel[];
  startWithDrawer?: boolean;
  openingDrag?: number;
  swipeOpened?: boolean;
  /** True while a cancelled opening swipe animates back off-screen. */
  settling?: boolean;
  /** Reports how much of the page is on screen (0..1) so the chat can slide out in lockstep. */
  onReveal?: (reveal: number, instant: boolean) => void;
}

type Destination = { friendId: string } | { groupId: string };
const EMPTY_MESSAGES: DirectMessage[] = [];
type ChatMessage = DirectMessage & {
  replyTo?: { id: string; content: string; senderId: string } | null;
};

function errorKey(
  error: unknown,
): 'directChat.notFound' | 'directChat.noPermission' | 'directChat.error' {
  if (error instanceof ApiError) {
    if (error.code === 'NOT_FOUND') return 'directChat.notFound';
    if (error.code === 'FORBIDDEN') return 'directChat.noPermission';
  }
  return 'directChat.error';
}

function initials(friend: ChatUser): string {
  return (friend.firstName || friend.username || String(friend.publicId))
    .split(/[\s_]+/)
    .slice(0, 2)
    .map((part) => part.slice(0, 1))
    .join('')
    .toUpperCase();
}

export function mergeMessages(
  current: DirectMessage[],
  incoming: DirectMessage[],
): DirectMessage[] {
  const messages = new Map(current.map((message) => [message.id, message]));
  for (const message of incoming) {
    const previous = messages.get(message.id);
    const latest =
      previous?.editedAt && (!message.editedAt || previous.editedAt > message.editedAt)
        ? { ...message, content: previous.content, editedAt: previous.editedAt }
        : message;
    if (!previous?.receipts?.length) {
      messages.set(message.id, latest);
      continue;
    }
    const receipts = new Map(message.receipts?.map((receipt) => [receipt.userId, receipt]) ?? []);
    for (const old of previous.receipts) {
      const next = receipts.get(old.userId);
      receipts.set(old.userId, {
        userId: old.userId,
        deliveredAt: next?.deliveredAt ?? old.deliveredAt,
        readAt: next?.readAt ?? old.readAt,
      });
    }
    messages.set(message.id, { ...latest, receipts: [...receipts.values()] });
  }
  return [...messages.values()].sort(compareDirectMessages);
}

/**
 * True when the polled window no longer overlaps the local log: everything the client holds is
 * older than the oldest server message, so messages in between were missed (e.g. a long
 * disconnect). The local pages are then stale and must be reloaded on demand.
 */
export function hasHistoryGap(current: DirectMessage[], incoming: DirectMessage[]): boolean {
  if (!current.length || !incoming.length) return false;
  const localIds = new Set(current.map((message) => message.id));
  if (incoming.some((message) => localIds.has(message.id))) return false;
  return Date.parse(incoming[0]!.createdAt) > Date.parse(current[current.length - 1]!.createdAt);
}

/** Replaces the receipts of one message (from a `receipt` event); unknown messages are ignored. */
export function applyReceiptEvent(
  current: DirectMessage[],
  messageId: string,
  receipts: NonNullable<DirectMessage['receipts']>,
): DirectMessage[] {
  const existing = current.find((message) => message.id === messageId);
  return existing ? mergeMessages(current, [{ ...existing, receipts }]) : current;
}

export function mergePolledMessages(
  current: DirectMessage[],
  incoming: DirectMessage[],
  changedSinceRequest: boolean,
): DirectMessage[] {
  if (hasHistoryGap(current, incoming)) return mergeMessages([], incoming);
  const serverIds = new Set(incoming.map((message) => message.id));
  // Older pages loaded on demand sit before the polled window and must survive polling.
  const windowStart = incoming.length ? incoming[0]!.createdAt : null;
  return mergeMessages(
    changedSinceRequest
      ? current
      : current.filter(
          (message) =>
            serverIds.has(message.id) || (windowStart !== null && message.createdAt < windowStart),
        ),
    incoming,
  );
}

export function completedSwipe(
  dx: number,
  dy: number,
  dir: 'ltr' | 'rtl',
  kind: 'screen' | 'drawer' | 'bubble' = 'screen',
): 'drawer' | 'close' | 'reply' | null {
  const logicalDx = dx * (dir === 'rtl' ? -1 : 1);
  if (Math.abs(dx) <= Math.abs(dy)) return null;
  if (kind === 'screen' && dx < -85) return 'drawer';
  if (kind === 'screen' && dx > 85) return 'close';
  if (kind === 'drawer' && dx > 85) return 'close';
  if (kind === 'bubble' && logicalDx < -65) return 'reply';
  return null;
}

export function draggedDrawerOffset(
  width: number,
  dx: number,
  _dir: 'ltr' | 'rtl',
  isOpen: boolean,
): number {
  const distance = Math.min(Math.max(isOpen ? dx : -dx, 0), width);
  return isOpen ? distance : width - distance;
}

export function drawerOffsetFromTransform(
  transform: string,
  width: number,
  isOpen: boolean,
): number {
  if (transform === 'none') return 0;
  const matrix = transform.match(/^matrix(3d)?\((.+)\)$/);
  if (!matrix) return isOpen ? 0 : width;
  const values = matrix[2]!.split(',').map(Number);
  const offset = values[matrix[1] ? 12 : 4];
  return offset !== undefined && Number.isFinite(offset)
    ? Math.max(0, Math.min(width, offset))
    : isOpen
      ? 0
      : width;
}

export function surfaceDrawerOffset(transform: string, width: number, isOpen: boolean): number {
  const fallback = isOpen ? 0 : width;
  if (transform === 'none') return width;
  const matrix = transform.match(/^matrix(3d)?\((.+)\)$/);
  if (!matrix) return fallback;
  const tx = matrix[2]!.split(',').map(Number)[matrix[1] ? 12 : 4];
  return tx !== undefined && Number.isFinite(tx)
    ? width - Math.max(0, Math.min(width, -tx))
    : fallback;
}

export function drawerBackdropOpacity(width: number, offset: number): number {
  return width > 0 ? Math.max(0, Math.min(1, 1 - offset / width)) : 0;
}

const MESSAGE_PAGE_SIZE = 50;
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '🔥', '👎'];

function chatParts(key: string): { kind: 'direct' | 'group'; id: string } {
  return key.startsWith('friend:')
    ? { kind: 'direct', id: key.slice(7) }
    : { kind: 'group', id: key.slice(6) };
}

interface ChatListCache {
  friends?: ChatUser[];
  groups?: GroupChat[];
  overview?: ChatOverview;
}

// Last known lists per api client, so reopening the section paints instantly.
const chatListCache = new WeakMap<object, ChatListCache>();

interface PendingFile {
  id: string;
  fileName: string;
  mime: string;
  previewUrl?: string;
}

export function receiptMark(receipt?: { deliveredAt: string | null; readAt: string | null }) {
  if (receipt?.readAt) return '✓✓';
  if (receipt?.deliveredAt) return '✓';
  return '';
}

export function receiptSummary(message: DirectMessage, recipientIds: string[]) {
  const receipts = message.receipts?.filter((entry) => recipientIds.includes(entry.userId)) ?? [];
  return {
    total: recipientIds.length,
    read: receipts.filter((entry) => !!entry.readAt).length,
    delivered: receipts.filter((entry) => !!entry.deliveredAt).length,
  };
}

export function visibleReceiptStatus(
  message: { top: number; bottom: number },
  log: { top: number; bottom: number },
  viewportHeight: number,
  focused: boolean,
): 'read' | 'delivered' | null {
  const height =
    Math.min(message.bottom, log.bottom, viewportHeight) - Math.max(message.top, log.top, 0);
  if (height <= 0) return null;
  return focused && height >= Math.min(24, (message.bottom - message.top) / 2)
    ? 'read'
    : 'delivered';
}

export function canAcknowledgeChat(
  visible: boolean,
  selected: boolean,
  obscured: boolean,
): boolean {
  return visible && selected && !obscured;
}

export function isSelected(current: { current: string | null }, key: string): boolean {
  return current.current === key;
}

function trapSubmenuTab(event: KeyboardEvent, container: HTMLElement | null) {
  const items = container?.querySelectorAll<HTMLElement>(
    'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  if (!items?.length) return;
  event.stopImmediatePropagation();
  const first = items[0]!;
  const last = items[items.length - 1]!;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function DirectChatScreen({
  api,
  publicId,
  userId,
  username,
  onClose,
  models,
  startWithDrawer = false,
  openingDrag,
  swipeOpened = false,
  settling = false,
  onReveal,
}: DirectChatScreenProps) {
  const { t, locale, dir } = useI18n();
  const screenRef = useRef<HTMLDivElement>(null);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const dialogRef = useFocusTrap(!modelPickerOpen && openingDrag === undefined, onClose);
  const logRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const drawerButtonRef = useRef<HTMLButtonElement>(null);
  const drawerCloseRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const drawerWasOpenRef = useRef(false);
  const screenCloseTimerRef = useRef<number | null>(null);
  const screenClosingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const groupNameRef = useRef<HTMLInputElement>(null);
  const messageMenuReturnRef = useRef<HTMLElement | null>(null);
  const requestRef = useRef(
    new Map<string, { content: string; replyToId?: string; id: string; fileKey: string }>(),
  );
  const refreshMessagesRef = useRef<{ key: string; run: () => Promise<void> } | null>(null);
  const listsRequestRef = useRef(0);
  const friendRevisionRef = useRef(0);
  const groupRevisionRef = useRef(0);
  const forwardRequestsRef = useRef(new Map<string, string>());
  const deletedRef = useRef(new Set<string>());
  const messageRevisionRef = useRef(0);
  const selectedRef = useRef<string | null>(null);
  const selectionVersionRef = useRef(0);
  const scrollRef = useRef<{ key: string; bottom: boolean; top: number } | null>(null);
  const receiptRef = useRef(new Set<string>());
  const gestureRef = useRef<{
    x: number;
    y: number;
    kind: 'screen' | 'drawer' | 'bubble';
    id?: string;
    pointerId: number;
    element: HTMLElement;
    tracking: boolean;
    drawerOffset: number;
    screenOffset: number;
  } | null>(null);
  const pressRef = useRef<number | null>(null);

  const aiButtonRef = useRef<HTMLButtonElement | null>(null);
  const aiRequestRef = useRef<
    Map<
      string,
      { key: string; target: Destination; promptId: string; modelId: string; requestId: string }
    >
  >(new Map());
  const aiAbortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const mountedRef = useRef(true);
  const forwardingRef = useRef(false);
  const editingRef = useRef<number | null>(null);
  const sendingRef = useRef(false);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);
  const pointerStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const [friends, setFriends] = useState<ChatUser[]>(() => chatListCache.get(api)?.friends ?? []);
  const [groups, setGroups] = useState<GroupChat[]>(() => chatListCache.get(api)?.groups ?? []);
  const [selected, setSelected] = useState<string | null>(null);
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [messageScope, setMessageScope] = useState<string | null>(null);
  const [loadingFriends, setLoadingFriends] = useState(() => !chatListCache.get(api)?.friends);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [drawerOpen, setDrawerOpen] = useState(startWithDrawer);
  const [drawerDrag, setDrawerDrag] = useState<number | null>(null);
  const [screenDrag, setScreenDrag] = useState<number | null>(null);
  const [screenDragging, setScreenDragging] = useState(false);
  const [screenClosing, setScreenClosing] = useState(false);
  const [replyId, setReplyId] = useState<string | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);
  useEffect(() => { if (menuId) hapticFeedback('tap'); }, [menuId]);
  const [selectingMessages, setSelectingMessages] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<string[]>([]);
  const [choosingDestination, setChoosingDestination] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editingVersion, setEditingVersion] = useState<number | null>(null);
  const [confirmDeleteForEveryoneId, setConfirmDeleteForEveryoneId] = useState<string | null>(null);
  const [groupForm, setGroupForm] = useState(false);
  const [groupTitle, setGroupTitle] = useState('');
  const [groupMembers, setGroupMembers] = useState<string[]>([]);
  const [friendLoadError, setFriendLoadError] = useState<string | null>(null);
  const [messageLoadError, setMessageLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [overview, setOverview] = useState<ChatOverview | null>(
    () => chatListCache.get(api)?.overview ?? null,
  );
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<ChatSearchResult[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<Record<string, PendingFile[]>>({});
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [pushState, setPushState] = useState<ChatPushState>('unsupported');
  const [pinnedMessages, setPinnedMessages] = useState<DirectMessage[]>([]);
  const liveRef = useRef(false);
  const wasDisconnectedRef = useRef(false);
  const refreshOverviewRef = useRef<(() => void) | null>(null);
  const refreshChatsRef = useRef<((options?: { quiet?: boolean }) => Promise<void>) | null>(null);
  const chatKeysRef = useRef(new Set<string>());
  const messagesRef = useRef<DirectMessage[]>([]);
  const typingTimerRef = useRef<number | null>(null);
  const pinCursorRef = useRef(0);
  const [presence, setPresence] = useState<{ online: boolean; typing: boolean }>({
    online: false,
    typing: false,
  });
  const lastTypingSentRef = useRef(0);
  const [hasOlder, setHasOlder] = useState(false);
  const [unreadFromId, setUnreadFromId] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const olderAnchorRef = useRef<number | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [addSheet, setAddSheet] = useState(false);
  const [chatMenuKey, setChatMenuKey] = useState<string | null>(null);
  const [renameKey, setRenameKey] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteChatKey, setDeleteChatKey] = useState<string | null>(null);
  const [chatBusy, setChatBusy] = useState(false);
  const rowPressRef = useRef<number | null>(null);
  const rowLongPressRef = useRef(false);
  const rowPressStartRef = useRef<{ x: number; y: number } | null>(null);
  const overviewRequestRef = useRef(0);

  const [internalModels, setInternalModels] = useState<AIModel[]>(models ?? []);
  const [aiPreferences, setAiPreferences] = useState(() => ({
    userId,
    ...readDirectChatPreferences(userId),
  }));
  const [aiPendingByChat, setAiPendingByChat] = useState<Record<string, number>>({});
  const [aiErrorsByChat, setAiErrorsByChat] = useState<
    Record<string, { text: string; requestId?: string; allowModelChange?: boolean } | undefined>
  >({});
  const accountPreferences =
    aiPreferences.userId === userId
      ? aiPreferences
      : { userId, ...readDirectChatPreferences(userId) };
  const updateAiPreferences = (next: { enabled: boolean; modelChoice: string }) => {
    writeDirectChatPreferences(userId, next);
    setAiPreferences({ userId, ...next });
  };
  const cancelGesture = useCallback(() => {
    if (pressRef.current !== null) window.clearTimeout(pressRef.current);
    pressRef.current = null;
    const gesture = gestureRef.current;
    gestureRef.current = null;
    if (gesture) {
      if (gesture.kind === 'bubble') gesture.element.style.transform = '';
      if (gesture.element.hasPointerCapture(gesture.pointerId))
        gesture.element.releasePointerCapture(gesture.pointerId);
    }
    setDrawerDrag(null);
    setScreenDragging(false);
    if (!screenClosingRef.current) setScreenDrag((current) => (current === null ? null : 0));
  }, []);

  useEffect(() => {
    if (models && models.length > 0) {
      setInternalModels(models);
      return;
    }
    let cancelled = false;
    api
      .getModels()
      .then((res) => {
        if (!cancelled && res.models) {
          setInternalModels(res.models);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, models]);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const cancelPresses = () => {
      cancelGesture();
      if (pointerStartPosRef.current) longPressTriggeredRef.current = true;
      clearLongPressTimer();
      pointerStartPosRef.current = null;
    };
    document.addEventListener('scroll', cancelPresses, true);
    document.addEventListener('visibilitychange', cancelPresses);
    window.addEventListener('blur', cancelPresses);
    return () => {
      cancelPresses();
      document.removeEventListener('scroll', cancelPresses, true);
      document.removeEventListener('visibilitychange', cancelPresses);
      window.removeEventListener('blur', cancelPresses);
    };
  }, [cancelGesture, clearLongPressTimer]);

  useEffect(() => {
    cancelGesture();
    clearLongPressTimer();
  }, [selected, cancelGesture, clearLongPressTimer]);

  useEffect(() => {
    mountedRef.current = true;
    const controllers = aiAbortControllersRef.current;
    return () => {
      mountedRef.current = false;
      clearLongPressTimer();
      for (const controller of controllers.values()) {
        controller.abort();
      }
      controllers.clear();
    };
  }, [clearLongPressTimer]);

  const handleAiPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    clearLongPressTimer();
    longPressTriggeredRef.current = false;
    pointerStartPosRef.current = { x: e.clientX, y: e.clientY };

    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      longPressTimerRef.current = null;
      setModelPickerOpen(true);
    }, 520);
  };

  const handleAiPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!pointerStartPosRef.current || longPressTimerRef.current === null) return;
    const dx = e.clientX - pointerStartPosRef.current.x;
    const dy = e.clientY - pointerStartPosRef.current.y;
    if (Math.hypot(dx, dy) > 10) {
      clearLongPressTimer();
      longPressTriggeredRef.current = true;
    }
  };

  const handleAiPointerUp = () => {
    clearLongPressTimer();
    pointerStartPosRef.current = null;
  };

  const handleAiPointerCancel = () => {
    clearLongPressTimer();
    pointerStartPosRef.current = null;
    longPressTriggeredRef.current = true;
  };

  const handleAiClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (longPressTriggeredRef.current && e.detail !== 0) {
      longPressTriggeredRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    longPressTriggeredRef.current = false;
    if (!selected) return;
    updateAiPreferences({ ...accountPreferences, enabled: !accountPreferences.enabled });
  };

  const handleAiKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (
      e.key === 'ArrowDown' ||
      e.key === 'F4' ||
      e.key === 'ContextMenu' ||
      (e.shiftKey && e.key === 'F10')
    ) {
      e.preventDefault();
      setModelPickerOpen(true);
    }
  };

  const handleCloseModelPicker = useCallback(() => {
    setModelPickerOpen(false);
    window.requestAnimationFrame(() => {
      aiButtonRef.current?.focus();
    });
  }, []);

  if (selectedRef.current !== selected) selectionVersionRef.current++;
  selectedRef.current = selected;
  const editing = editingVersion === selectionVersionRef.current;
  const isAiEnabled = accountPreferences.enabled;
  const chatModelChoice = accountPreferences.modelChoice;
  const resolvedChatModelId = resolveDirectChatModelId(internalModels, chatModelChoice);
  const selectedFriend = selected?.startsWith('friend:')
    ? friends.find((friend) => friend.id === selected.slice(7))
    : null;
  const selectedGroup = selected?.startsWith('group:')
    ? groups.find((group) => group.id === selected.slice(6))
    : null;
  const destination: Destination | null = selectedFriend
    ? { friendId: selectedFriend.id }
    : selectedGroup
      ? { groupId: selectedGroup.id }
      : null;
  const aliases = aliasMap(overview);
  const labelOf = (friend: ChatUser) => aliases.get(`friend:${friend.id}`) ?? friendLabel(friend);
  const chatEntries = buildChatList({ friends, groups, overview, userId });
  const listEntries = visibleChatList(chatEntries, { archived: showArchived });
  const archivedCount = visibleChatList(chatEntries, { archived: true }).length;
  const strip = friendStrip(chatEntries);
  const selectedEntry = chatEntries.find((entry) => entry.key === selected);
  const chatMenuEntry = chatEntries.find((entry) => entry.key === chatMenuKey);
  const renameEntry = chatEntries.find((entry) => entry.key === renameKey);
  const deleteEntry = chatEntries.find((entry) => entry.key === deleteChatKey);
  const sheetOpen = addSheet || groupForm || !!chatMenuKey || !!renameKey || !!deleteChatKey;
  const visibleMessages = messageScope === selected ? messages : EMPTY_MESSAGES;
  messagesRef.current = messages;
  chatKeysRef.current = new Set([
    ...friends.map((friend) => `friend:${friend.id}`),
    ...groups.map((group) => `group:${group.id}`),
  ]);
  const drawerWidth = drawerRef.current?.offsetWidth || Math.min(window.innerWidth * 0.85, 340);
  const trackedDrawerOffset = openingDrag === undefined ? drawerDrag : null;
  const forwardIds = orderedForwardIds(visibleMessages, selectedMessageIds);
  const toggleMessageSelection = (id: string) =>
    setSelectedMessageIds((current) => toggleDirectMessageSelection(current, id));
  const draft = selected ? (drafts[selected] ?? '') : '';
  const reply = visibleMessages.find((message) => message.id === replyId);
  const memberUsers: ChatUser[] =
    (selectedGroup as (GroupChat & { members?: ChatUser[] }) | null)?.members ??
    friends.filter((friend) => selectedGroup?.memberIds?.includes(friend.id));

  useEffect(() => {
    if (!selectingMessages) return;
    const ids = new Set(visibleMessages.map((message) => message.id));
    setSelectedMessageIds((current) => {
      const remaining = current.filter((id) => ids.has(id));
      return remaining.length === current.length ? current : remaining;
    });
  }, [selectingMessages, visibleMessages]);

  useEffect(() => {
    if (openingDrag !== undefined) return;
    return lockBodyScroll({ allowTouchMoveWithin: screenRef.current });
  }, [openingDrag]);
  useEffect(
    () => () => {
      listsRequestRef.current++;
      if (pressRef.current !== null) window.clearTimeout(pressRef.current);
      if (screenCloseTimerRef.current !== null) window.clearTimeout(screenCloseTimerRef.current);
    },
    [],
  );

  const refreshChats = useCallback(async (options?: { quiet?: boolean }) => {
    const requestId = ++listsRequestRef.current;
    const friendRevision = friendRevisionRef.current;
    const groupRevision = groupRevisionRef.current;
    // A background refresh (realtime event) must not blank the list with a loading state.
    if (!options?.quiet) setLoadingFriends(true);
    setFriendLoadError(null);
    try {
      // The overview is heavy; it must not hold back the list itself.
      void api
        .getDirectChatOverview()
        .then((value) => {
          const cache = chatListCache.get(api) ?? {};
          chatListCache.set(api, { ...cache, overview: value });
          if (listsRequestRef.current === requestId) setOverview(value);
        })
        .catch(() => undefined);
      const [friendResult, groupResult] = await Promise.allSettled([
        api.listDirectChatFriends(),
        api.listDirectChatGroups(),
      ]);
      if (listsRequestRef.current !== requestId) return;
      if (friendResult.status === 'rejected' && groupResult.status === 'rejected') {
        throw new Error('Chat lists unavailable');
      }
      const friendResponse =
        friendResult.status === 'fulfilled' && friendRevision === friendRevisionRef.current
          ? friendResult.value
          : null;
      const groupResponse =
        groupResult.status === 'fulfilled' && groupRevision === groupRevisionRef.current
          ? groupResult.value
          : null;
      if (friendResponse) setFriends(friendResponse.friends);
      if (groupResponse) setGroups(groupResponse.groups);
      chatListCache.set(api, {
        ...chatListCache.get(api),
        ...(friendResponse ? { friends: friendResponse.friends } : {}),
        ...(groupResponse ? { groups: groupResponse.groups } : {}),
      });
      if (friendResult.status === 'rejected' || groupResult.status === 'rejected')
        setActionError(t('directChat.error'));
      setSelected((current) =>
        current &&
        (current.startsWith('friend:')
          ? !friendResponse ||
            friendResponse.friends.some((friend) => friend.id === current.slice(7))
          : !groupResponse || groupResponse.groups.some((group) => group.id === current.slice(6)))
          ? current
          : null,
      );
    } catch {
      if (listsRequestRef.current === requestId) setFriendLoadError(t('directChat.error'));
    } finally {
      if (listsRequestRef.current === requestId) setLoadingFriends(false);
    }
  }, [api, t]);

  refreshChatsRef.current = refreshChats;
  useEffect(() => {
    void refreshChats();
  }, [refreshChats]);

  useEffect(() => {
    if (selected || openingDrag !== undefined) return;
    let active = true;
    const refreshOverview = async () => {
      const requestId = ++overviewRequestRef.current;
      try {
        const next = await api.getDirectChatOverview();
        if (active && overviewRequestRef.current === requestId)
          setOverview((current) =>
            current && JSON.stringify(current) === JSON.stringify(next) ? current : next,
          );
      } catch {
        // The list keeps its previous state; the next poll retries.
      }
    };
    void refreshOverview();
    refreshOverviewRef.current = () => void refreshOverview();
    let tick = 0;
    const interval = window.setInterval(() => {
      if (liveRef.current && ++tick % 4 !== 0) return;
      if (!document.hidden) void refreshOverview();
    }, 15_000);
    return () => {
      active = false;
      refreshOverviewRef.current = null;
      window.clearInterval(interval);
    };
  }, [api, selected, openingDrag]);

  useEffect(() => {
    let active = true;
    getChatPushState(api)
      .then((state) => {
        if (active) setPushState(state);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [api]);

  const togglePush = async () => {
    try {
      setPushState(pushState === 'on' ? await disableChatPush(api) : await enableChatPush(api));
    } catch {
      setActionError(t('directChat.error'));
    }
  };

  useEffect(() => {
    const stop = api.subscribeDirectChatEvents(
      (event) => {
        const key = `${event.kind === 'direct' ? 'friend' : 'group'}:${event.targetId}`;
        const open = refreshMessagesRef.current?.key === key;
        if (event.type === 'typing') {
          // Typing only moves the indicator; the next poll or message event corrects it.
          if (!open) return;
          setPresence((current) => (current.typing ? current : { ...current, typing: true }));
          if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
          typingTimerRef.current = window.setTimeout(() => {
            typingTimerRef.current = null;
            setPresence((current) => (current.typing ? { ...current, typing: false } : current));
          }, 5_000);
          return;
        }
        refreshOverviewRef.current?.();
        if (event.type === 'receipt') {
          if (open && event.messageId && event.receipts)
            setMessages((current) =>
              applyReceiptEvent(current, event.messageId!, event.receipts!),
            );
          return;
        }
        // A chat we do not know yet: a new friend or group, so the lists must be reloaded.
        if (!chatKeysRef.current.has(key)) void refreshChatsRef.current?.({ quiet: true });
        if (event.deletedIds?.length) {
          for (const id of event.deletedIds) deletedRef.current.add(id);
          if (open) {
            const gone = new Set(event.deletedIds);
            setMessages((current) => current.filter((message) => !gone.has(message.id)));
          }
        }
        if (open) void refreshMessagesRef.current?.run();
      },
      (live) => {
        liveRef.current = live;
        if (!live) {
          wasDisconnectedRef.current = true;
          return;
        }
        // Events sent while the stream was down are lost: reload right after a reconnect.
        if (!wasDisconnectedRef.current) return;
        wasDisconnectedRef.current = false;
        refreshOverviewRef.current?.();
        void refreshMessagesRef.current?.run();
      },
    );
    return () => {
      liveRef.current = false;
      if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
      stop();
    };
  }, [api]);

  useEffect(() => {
    const query = search.trim();
    if (query.length < 2) {
      setSearchResults(null);
      return;
    }
    let active = true;
    setSearchResults(null);
    const timer = window.setTimeout(() => {
      api
        .searchDirectChats(query)
        .then((response) => {
          if (active) setSearchResults(response.results);
        })
        .catch(() => {
          if (active) setSearchResults([]);
        });
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [api, search]);

  useEffect(() => {
    if (!selected) {
      refreshMessagesRef.current = null;
      setMessageScope(null);
      setMessages([]);
      setLoadingMessages(false);
      return;
    }
    const target: Destination = selected.startsWith('friend:')
      ? { friendId: selected.slice(7) }
      : { groupId: selected.slice(6) };
    let active = true;
    let inFlight = false;
    // A refresh requested while one is in flight runs once more afterwards: its trigger (a
    // realtime event) may describe a change the current request does not contain yet.
    let dirty = false;
    let firstPage = true;
    const refreshMessages = async (): Promise<void> => {
      if (inFlight) {
        dirty = true;
        return;
      }
      inFlight = true;
      const revision = messageRevisionRef.current;
      try {
        const response =
          'friendId' in target
            ? await api.listDirectMessages(target.friendId, { limit: MESSAGE_PAGE_SIZE })
            : await api.listGroupMessages(target.groupId, { limit: MESSAGE_PAGE_SIZE });
        if (!active || !isSelected(selectedRef, selected)) return;
        const nextPresence = response.peer ?? {
          online: false,
          typing: !!response.typingIds?.length,
        };
        setPresence((current) =>
          current.online === nextPresence.online && current.typing === nextPresence.typing
            ? current
            : nextPresence,
        );
        const nextPinned = response.pinned ?? [];
        setPinnedMessages((current) =>
          JSON.stringify(current) === JSON.stringify(nextPinned) ? current : nextPinned,
        );
        if (firstPage) {
          firstPage = false;
          setHasOlder(!!response.hasMore);
          const firstUnread = response.messages.find(
            (message) =>
              message.senderId !== userId &&
              !message.receipts?.find((entry) => entry.userId === userId)?.readAt,
          );
          setUnreadFromId(firstUnread?.id ?? null);
        }
        const serverMessages = response.messages.filter(
          (message) => !deletedRef.current.has(message.id),
        );
        // After a long disconnect the window may not reach the local log: older pages are
        // dropped by the merge, and the user can load them again on demand.
        if (hasHistoryGap(messagesRef.current, serverMessages)) setHasOlder(!!response.hasMore);
        setMessages((current) => {
          const merged = mergePolledMessages(
            current,
            serverMessages,
            revision !== messageRevisionRef.current,
          );
          // Keep the old array when nothing changed so the whole log is not re-rendered.
          return JSON.stringify(merged) === JSON.stringify(current) ? current : merged;
        });
        setMessageLoadError(null);
      } catch (error) {
        if (active && isSelected(selectedRef, selected)) setMessageLoadError(t(errorKey(error)));
      } finally {
        inFlight = false;
        if (active && isSelected(selectedRef, selected)) setLoadingMessages(false);
        if (dirty && active) {
          dirty = false;
          void refreshMessages();
        }
      }
    };
    refreshMessagesRef.current = { key: selected, run: refreshMessages };
    setMessageScope(selected);
    setMessages([]);
    setHasOlder(false);
    setPresence({ online: false, typing: false });
    setPinnedMessages([]);
    pinCursorRef.current = 0;
    setUnreadFromId(null);
    setLoadingOlder(false);
    setReplyId(null);
    setMenuId(null);
    setEditId(null);
    setSelectingMessages(false);
    setSelectedMessageIds([]);
    setChoosingDestination(false);
    setMessageLoadError(null);
    setLoadingMessages(true);
    void refreshMessages();
    let tick = 0;
    const interval = window.setInterval(() => {
      // Realtime events drive updates; polling is only a slow safety net while connected.
      if (liveRef.current && ++tick % 6 !== 0) return;
      if (!document.hidden) void refreshMessages();
    }, 4_000);
    return () => {
      active = false;
      if (refreshMessagesRef.current?.run === refreshMessages) refreshMessagesRef.current = null;
      window.clearInterval(interval);
    };
  }, [api, selected, t, userId]);

  useLayoutEffect(() => {
    const log = logRef.current;
    if (!log || !selected) return;
    const previous = scrollRef.current;
    if (olderAnchorRef.current !== null) {
      log.scrollTop += log.scrollHeight - olderAnchorRef.current;
      olderAnchorRef.current = null;
    } else if (!previous || previous.key !== selected || previous.bottom) {
      log.scrollTop = log.scrollHeight;
    } else {
      log.scrollTop = previous.top;
    }
    scrollRef.current = {
      key: selected,
      top: log.scrollTop,
      bottom: log.scrollHeight - log.scrollTop - log.clientHeight < 12,
    };
  }, [visibleMessages, selected]);

  const loadOlderMessages = async () => {
    const key = selected;
    const oldest = messages[0];
    const log = logRef.current;
    if (!key || !oldest || !log || loadingOlder || !hasOlder) return;
    setLoadingOlder(true);
    try {
      const page = { limit: MESSAGE_PAGE_SIZE, before: oldest.createdAt };
      const response = key.startsWith('friend:')
        ? await api.listDirectMessages(key.slice(7), page)
        : await api.listGroupMessages(key.slice(6), page);
      if (!isSelected(selectedRef, key)) return;
      olderAnchorRef.current = log.scrollHeight;
      setMessages((current) => mergeMessages(current, response.messages));
      setHasOlder(!!response.hasMore);
    } catch (error) {
      if (isSelected(selectedRef, key)) setActionError(t(errorKey(error)));
    } finally {
      setLoadingOlder(false);
    }
  };

  useEffect(() => {
    if (!selected || !visibleMessages.length) return;
    const destination: Destination = selected.startsWith('friend:')
      ? { friendId: selected.slice(7) }
      : { groupId: selected.slice(6) };
    let frame: number | null = null;
    const acknowledge = () => {
      if (
        !canAcknowledgeChat(
          document.visibilityState === 'visible',
          isSelected(selectedRef, selected),
          drawerOpen || !!menuId,
        )
      )
        return;
      const log = logRef.current;
      if (!log) return;
      const logBounds = log.getBoundingClientRect();
      for (const message of visibleMessages) {
        if (message.senderId === userId) continue;
        const bubble = log.querySelector<HTMLElement>(`[data-message-id="${message.id}"]`);
        if (!bubble) continue;
        const status = visibleReceiptStatus(
          bubble.getBoundingClientRect(),
          logBounds,
          window.visualViewport?.height ?? window.innerHeight,
          document.hasFocus(),
        );
        if (!status) continue;
        const receipt = message.receipts?.find((entry) => entry.userId === userId);
        if (status === 'read' ? receipt?.readAt : receipt?.deliveredAt) continue;
        const key = `${message.id}:${status}`;
        if (receiptRef.current.has(key)) continue;
        receiptRef.current.add(key);
        void api
          .markDirectChatReceipt(destination, message.id, status)
          .then(({ message: updated }) => {
            if (isSelected(selectedRef, selected))
              setMessages((current) => mergeMessages(current, [updated]));
          })
          .catch(() => {
            receiptRef.current.delete(key);
          });
      }
    };
    const scheduleAcknowledge = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        acknowledge();
      });
    };
    scheduleAcknowledge();
    document.addEventListener('visibilitychange', scheduleAcknowledge);
    window.addEventListener('focus', scheduleAcknowledge);
    window.addEventListener('resize', scheduleAcknowledge);
    const log = logRef.current;
    log?.addEventListener('scroll', scheduleAcknowledge);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', scheduleAcknowledge);
      window.removeEventListener('focus', scheduleAcknowledge);
      window.removeEventListener('resize', scheduleAcknowledge);
      log?.removeEventListener('scroll', scheduleAcknowledge);
    };
  }, [api, selected, visibleMessages, userId, drawerOpen, menuId]);

  useEffect(() => {
    if (openingDrag !== undefined) return;
    if (drawerOpen) drawerCloseRef.current?.focus();
    else if (drawerWasOpenRef.current) drawerButtonRef.current?.focus();
    drawerWasOpenRef.current = drawerOpen;
  }, [drawerOpen, openingDrag]);

  useEffect(() => {
    if (groupForm) groupNameRef.current?.focus();
  }, [groupForm]);

  useEffect(() => {
    if (!menuId) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        if (forwardingRef.current) return;
        setMenuId(null);
        messageMenuReturnRef.current?.focus();
      } else if (event.key === 'Tab') {
        trapSubmenuTab(event, menuRef.current);
      }
    };
    document.addEventListener('keydown', close, true);
    return () => document.removeEventListener('keydown', close, true);
  }, [menuId]);

  useEffect(() => {
    if (!drawerOpen || menuId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        if (forwardingRef.current) return;
        if (groupForm) setGroupForm(false);
        else {
          setChoosingDestination(false);
          setDrawerOpen(false);
        }
      } else if (event.key === 'Tab') {
        trapSubmenuTab(event, drawerRef.current);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [drawerOpen, menuId, groupForm]);

  useEffect(() => {
    if (!sheetOpen || drawerOpen || menuId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      if (chatBusy) return;
      setAddSheet(false);
      setGroupForm(false);
      setChatMenuKey(null);
      setRenameKey(null);
      setDeleteChatKey(null);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [sheetOpen, drawerOpen, menuId, chatBusy]);

  const selectChat = (key: string) => {
    cancelGesture();
    clearLongPressTimer();
    setModelPickerOpen(false);
    if (selectedRef.current !== key) selectionVersionRef.current++;
    selectedRef.current = key;
    setSelected(key);
    setDrawerOpen(false);
    setAddSheet(false);
    setChatMenuKey(null);
    setChoosingDestination(false);
    setSelectingMessages(false);
    setSelectedMessageIds([]);
    setEditId(null);
    setActionError(null);
    window.requestAnimationFrame(() => composerRef.current?.focus());
  };

  const backToList = () => {
    cancelGesture();
    clearLongPressTimer();
    setModelPickerOpen(false);
    selectedRef.current = null;
    selectionVersionRef.current++;
    setSelected(null);
    setDrawerOpen(false);
    setChoosingDestination(false);
    setSelectingMessages(false);
    setSelectedMessageIds([]);
    setEditId(null);
    setReplyId(null);
    setMenuId(null);
    setActionError(null);
  };

  const endRowPress = () => {
    if (rowPressRef.current !== null) window.clearTimeout(rowPressRef.current);
    rowPressRef.current = null;
    rowPressStartRef.current = null;
  };
  const startRowPress = (key: string, point: { clientX: number; clientY: number }) => {
    endRowPress();
    rowLongPressRef.current = false;
    rowPressStartRef.current = { x: point.clientX, y: point.clientY };
    rowPressRef.current = window.setTimeout(() => {
      rowPressRef.current = null;
      rowLongPressRef.current = true;
      setChatMenuKey(key);
    }, 500);
  };
  const moveRowPress = (point: { clientX: number; clientY: number }) => {
    const start = rowPressStartRef.current;
    if (start && Math.hypot(point.clientX - start.x, point.clientY - start.y) > 10) endRowPress();
  };
  const openFromList = (key: string) => {
    if (rowLongPressRef.current) {
      rowLongPressRef.current = false;
      return;
    }
    selectChat(key);
  };
  const closeChatMenu = () => {
    rowLongPressRef.current = false;
    setChatMenuKey(null);
  };

  const patchOverview = (update: (current: ChatOverview) => ChatOverview) =>
    setOverview((current) => update(current ?? { prefs: [], blockedIds: [], summaries: [] }));

  const updateChatPref = async (key: string, patch: ChatPrefPatch) => {
    const entry = chatEntries.find((item) => item.key === key);
    if (!entry || chatBusy) return;
    setChatBusy(true);
    setActionError(null);
    try {
      const { pref } = await api.updateDirectChatPref(entry.kind, entry.id, patch);
      patchOverview((current) => ({
        ...current,
        prefs: [
          ...current.prefs.filter(
            (item) => !(item.kind === pref.kind && item.targetId === pref.targetId),
          ),
          pref,
        ],
      }));
      setChatMenuKey(null);
      setRenameKey(null);
    } catch (error) {
      setActionError(t(errorKey(error)));
    } finally {
      if (mountedRef.current) setChatBusy(false);
    }
  };

  const toggleChatBlock = async (key: string) => {
    const entry = chatEntries.find((item) => item.key === key);
    if (!entry || entry.kind !== 'direct' || chatBusy) return;
    setChatBusy(true);
    setActionError(null);
    try {
      await api.setDirectChatBlocked(entry.id, !entry.blocked);
      patchOverview((current) => ({
        ...current,
        blockedIds: entry.blocked
          ? current.blockedIds.filter((id) => id !== entry.id)
          : [...current.blockedIds, entry.id],
      }));
      setChatMenuKey(null);
    } catch (error) {
      setActionError(t(errorKey(error)));
    } finally {
      if (mountedRef.current) setChatBusy(false);
    }
  };

  const deleteChat = async (key: string, scope: 'me' | 'everyone') => {
    const entry = chatEntries.find((item) => item.key === key);
    if (!entry || chatBusy) return;
    setChatBusy(true);
    setActionError(null);
    try {
      await api.deleteDirectChat(entry.kind, entry.id, scope);
      if (!mountedRef.current) return;
      if (entry.kind === 'group') {
        groupRevisionRef.current++;
        setGroups((current) => current.filter((group) => group.id !== entry.id));
      }
      patchOverview((current) => ({
        ...current,
        prefs: current.prefs.map((pref) =>
          pref.kind === entry.kind && pref.targetId === entry.id
            ? { ...pref, pinnedAt: null, archivedAt: null }
            : pref,
        ),
        summaries: current.summaries.map((summary) =>
          summary.kind === entry.kind && summary.targetId === entry.id
            ? { ...summary, lastMessage: null, unreadCount: 0 }
            : summary,
        ),
      }));
      setDeleteChatKey(null);
      if (selectedRef.current === key) backToList();
    } catch (error) {
      setActionError(t(errorKey(error)));
    } finally {
      if (mountedRef.current) setChatBusy(false);
    }
  };

  const addFriend = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = identifier.trim();
    if (!value || adding) return;
    if (
      value === String(publicId) ||
      (value.startsWith('@') && username && value.slice(1).toLowerCase() === username.toLowerCase())
    ) {
      setActionError(t('directChat.addSelf'));
      return;
    }
    setAdding(true);
    setActionError(null);
    try {
      const { friend } = await api.addDirectChatFriend(value);
      friendRevisionRef.current++;
      setFriends((current) =>
        current.some((item) => item.id === friend.id) ? current : [...current, friend],
      );
      selectChat(`friend:${friend.id}`);
      setIdentifier('');
    } catch (error) {
      setActionError(
        error instanceof ApiError && error.code === 'NOT_FOUND'
          ? t('directChat.notFound')
          : t('directChat.addError'),
      );
    } finally {
      setAdding(false);
    }
  };

  const createGroup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const title = groupTitle.trim();
    if (!title || !groupMembers.length || groupMembers.length > 49 || creating) return;
    setCreating(true);
    setActionError(null);
    try {
      const { group } = await api.createDirectChatGroup(title, groupMembers);
      groupRevisionRef.current++;
      setGroups((current) => [...current, group]);
      setGroupForm(false);
      setGroupTitle('');
      setGroupMembers([]);
      selectChat(`group:${group.id}`);
    } catch (error) {
      setActionError(t(errorKey(error)));
    } finally {
      setCreating(false);
    }
  };

  const uploadFiles = async (list: ArrayLike<File> | null, kind: 'image' | 'file') => {
    const key = selected;
    if (!key || !list || list.length === 0) return;
    setAttaching(true);
    setActionError(null);
    try {
      const uploaded = await Promise.all(
        Array.from(list).map(async (file): Promise<PendingFile> => {
          const prepared =
            kind === 'image'
              ? await imageFileToDataUrl(file)
              : {
                  fileName: file.name || 'file',
                  mime: file.type.split(';')[0] || 'application/octet-stream',
                  dataUrl: await readFileAsDataUrl(file),
                };
          const { attachment } = await api.uploadAttachment({
            fileName: prepared.fileName,
            mime: prepared.mime,
            dataBase64: dataUrlToBase64(prepared.dataUrl),
          });
          return {
            id: attachment.id,
            fileName: attachment.fileName,
            mime: attachment.mime,
            previewUrl: kind === 'image' ? prepared.dataUrl : undefined,
          };
        }),
      );
      setPendingFiles((current) => ({
        ...current,
        [key]: [...(current[key] ?? []), ...uploaded].slice(0, 10),
      }));
    } catch (error) {
      setActionError(t(errorKey(error)));
    } finally {
      setAttaching(false);
    }
  };

  const sendMessage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const key = selected;
    const target = destination;
    const content = draft.trim();
    const files = pendingFiles[key ?? ''] ?? [];
    if (
      !key ||
      !target ||
      (!content && !files.length) ||
      attaching ||
      sendingRef.current ||
      !isSelected(selectedRef, key)
    )
      return;
    const attachmentIds = files.map((file) => file.id);
    const fileKey = attachmentIds.join(',');

    // Capture AI settings at submission time so mid-flight toggles don't affect this message
    const shouldReplyWithAi = accountPreferences.enabled;
    const chosenModelChoice = accountPreferences.modelChoice;
    const chosenModelId =
      resolveDirectChatModelId(internalModels, chosenModelChoice) ?? AUTO_MODEL_CHOICE_ID;

    let pending = requestRef.current.get(key);
    if (
      !pending ||
      pending.content !== content ||
      pending.replyToId !== (replyId ?? undefined) ||
      pending.fileKey !== fileKey
    ) {
      pending = { content, replyToId: replyId ?? undefined, id: crypto.randomUUID(), fileKey };
      requestRef.current.set(key, pending);
    }
    setSending(true);
    sendingRef.current = true;
    setActionError(null);
    let sentMessage: DirectMessage;
    try {
      const { message } =
        'friendId' in target
          ? attachmentIds.length
            ? await api.sendDirectMessage(
                target.friendId,
                content,
                pending.id,
                pending.replyToId,
                attachmentIds,
              )
            : await api.sendDirectMessage(target.friendId, content, pending.id, pending.replyToId)
          : attachmentIds.length
            ? await api.sendGroupMessage(
                target.groupId,
                content,
                pending.id,
                pending.replyToId,
                attachmentIds,
              )
            : await api.sendGroupMessage(target.groupId, content, pending.id, pending.replyToId);
      sentMessage = message;
      if (requestRef.current.get(key)?.id === pending.id) requestRef.current.delete(key);
      if (!mountedRef.current) return;
      setDrafts((current) =>
        current[key]?.trim() === content ? { ...current, [key]: '' } : current,
      );
      // Only the files that went out are dropped: ones attached meanwhile stay pending.
      if (attachmentIds.length) {
        const sent = new Set(attachmentIds);
        setPendingFiles((current) => {
          const left = (current[key] ?? []).filter((file) => !sent.has(file.id));
          return left.length === (current[key] ?? []).length
            ? current
            : { ...current, [key]: left };
        });
      }
      if (isSelected(selectedRef, key)) {
        messageRevisionRef.current++;
        setMessages((current) => mergeMessages(current, [message]));
        setReplyId(null);
      }
    } catch (error) {
      if (mountedRef.current && isSelected(selectedRef, key)) setActionError(t(errorKey(error)));
      return;
    } finally {
      sendingRef.current = false;
      if (mountedRef.current) setSending(false);
    }

    if (shouldReplyWithAi && sentMessage && mountedRef.current) {
      const request = {
        key,
        target,
        promptId: sentMessage.id,
        modelId: chosenModelId,
        requestId: crypto.randomUUID(),
      };
      aiRequestRef.current.set(request.requestId, request);
      void generateAiReply(request.requestId);
    }
  };

  const generateAiReply = async (requestId: string) => {
    const request = aiRequestRef.current.get(requestId);
    if (!request || aiAbortControllersRef.current.has(requestId)) return;
    const { key, target, promptId, modelId } = request;
    const controller = new AbortController();
    aiAbortControllersRef.current.set(requestId, controller);
    setAiPendingByChat((current) => ({ ...current, [key]: (current[key] ?? 0) + 1 }));
    setAiErrorsByChat((current) => ({ ...current, [key]: undefined }));
    try {
      const { message } = await api.replyDirectChatAi(
        target,
        promptId,
        modelId,
        requestId,
        controller.signal,
      );
      aiRequestRef.current.delete(requestId);
      if (mountedRef.current && isSelected(selectedRef, key)) {
        messageRevisionRef.current++;
        setMessages((current) => mergeMessages(current, [message]));
      }
    } catch (error) {
      if (mountedRef.current && !controller.signal.aborted) {
        const configurationError =
          error instanceof ApiError &&
          ['MODEL_UNAVAILABLE', 'MODEL_NOT_SUPPORTED', 'NOT_FOUND'].includes(error.code);
        const text = configurationError
          ? t('directChat.aiConfigurationError')
          : t('directChat.aiGenerationError', {
              error: error instanceof ApiError ? error.message : t('directChat.error'),
            });
        setAiErrorsByChat((current) => ({
          ...current,
          [key]: { text, requestId, allowModelChange: configurationError },
        }));
      }
    } finally {
      if (aiAbortControllersRef.current.get(requestId) === controller)
        aiAbortControllersRef.current.delete(requestId);
      if (mountedRef.current)
        setAiPendingByChat((current) => ({
          ...current,
          [key]: Math.max(0, (current[key] ?? 1) - 1),
        }));
    }
  };

  const saveEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const key = selected;
    const target = destination;
    const id = editId;
    const content = editContent.trim();
    const version = selectionVersionRef.current;
    if (!key || !target || !id || !content || editingRef.current === version) return;
    editingRef.current = version;
    setEditingVersion(version);
    setActionError(null);
    try {
      const { message } = await api.editDirectMessage(target, id, content);
      if (mountedRef.current && isSelected(selectedRef, key)) {
        messageRevisionRef.current++;
        setMessages((current) => mergeMessages(current, [message]));
        if (selectionVersionRef.current === version) setEditId(null);
      }
    } catch (error) {
      if (
        mountedRef.current &&
        isSelected(selectedRef, key) &&
        selectionVersionRef.current === version
      )
        setActionError(t(errorKey(error)));
    } finally {
      if (editingRef.current === version) editingRef.current = null;
      if (mountedRef.current)
        setEditingVersion((current) => (current === version ? null : current));
    }
  };

  const toggleReaction = async (messageId: string, emoji: string) => {
    if (!selected) return;
    const { kind, id } = chatParts(selected);
    const mine = messages
      .find((message) => message.id === messageId)
      ?.reactions?.find((reaction) => reaction.userId === userId);
    const next = mine?.emoji === emoji ? null : emoji;
    setMessages((current) =>
      current.map((message) =>
        message.id === messageId
          ? {
              ...message,
              reactions: [
                ...(message.reactions ?? []).filter((reaction) => reaction.userId !== userId),
                ...(next ? [{ userId, emoji: next }] : []),
              ],
            }
          : message,
      ),
    );
    try {
      await api.setDirectChatReaction(kind, id, messageId, next);
    } catch {
      setActionError(t('directChat.error'));
      void refreshMessagesRef.current?.run();
    }
  };

  const togglePin = async (messageId: string) => {
    if (!selected) return;
    const { kind, id } = chatParts(selected);
    const target = messages.find((message) => message.id === messageId);
    if (!target) return;
    const pinned = !target.pinned;
    const stamp = pinned ? { by: userId, at: new Date().toISOString() } : null;
    setMessages((current) =>
      current.map((message) =>
        message.id === messageId ? { ...message, pinned: stamp } : message,
      ),
    );
    setPinnedMessages((current) =>
      pinned
        ? [{ ...target, pinned: stamp }, ...current.filter((message) => message.id !== messageId)]
        : current.filter((message) => message.id !== messageId),
    );
    pinCursorRef.current = 0;
    try {
      await api.setDirectChatPin(kind, id, messageId, pinned);
    } catch {
      setActionError(t('directChat.error'));
      void refreshMessagesRef.current?.run();
    }
  };

  const showPinned = () => {
    const pins = pinnedMessages;
    if (!pins.length) return;
    const index = pinCursorRef.current % pins.length;
    pinCursorRef.current = index + 1;
    const bubble = logRef.current?.querySelector<HTMLElement>(
      `[data-message-id="${pins[index]!.id}"]`,
    );
    bubble?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const deleteMessage = async (id: string, options?: { forEveryone?: boolean }) => {
    setMenuId(null);
    const key = selected;
    const target = destination;
    try {
      if (!target || !key) return;
      await api.deleteDirectMessage(target, id, options);
      deletedRef.current.add(id);
      if (mountedRef.current && isSelected(selectedRef, key)) {
        setMessages((current) => current.filter((message) => message.id !== id));
        if (replyId === id) setReplyId(null);
        if (editId === id) setEditId(null);
      }
    } catch (error) {
      if (mountedRef.current && key && isSelected(selectedRef, key))
        setActionError(t(errorKey(error)));
    }
    if (key && isSelected(selectedRef, key)) messageMenuReturnRef.current?.focus();
  };

  const forwardMessage = async (target: Destination) => {
    if (!forwardIds.length || !destination || forwardingRef.current) return;
    const ids = forwardIds;
    const source = destination;
    const sourceKey = selected;
    const sourceVersion = selectionVersionRef.current;
    const key = `${sourceKey}:${ids.join(',')}:${'friendId' in target ? `friend:${target.friendId}` : `group:${target.groupId}`}`;
    let clientRequestId = forwardRequestsRef.current.get(key);
    if (!clientRequestId) {
      clientRequestId = crypto.randomUUID();
      forwardRequestsRef.current.set(key, clientRequestId);
    }
    setForwarding(true);
    forwardingRef.current = true;
    setActionError(null);
    try {
      const { messages: forwarded } = await api.forwardChatMessages({
        sourceKind: 'friendId' in source ? 'direct' : 'group',
        sourceConversationId: 'friendId' in source ? source.friendId : source.groupId,
        messageIds: ids,
        targetKind: 'friendId' in target ? 'direct' : 'group',
        targetConversationId: 'friendId' in target ? target.friendId : target.groupId,
        clientRequestId,
      });
      if (
        mountedRef.current &&
        isSelected(selectedRef, sourceKey ?? '') &&
        selectionVersionRef.current === sourceVersion
      ) {
        forwardRequestsRef.current.delete(key);
        if (JSON.stringify(target) === JSON.stringify(source)) {
          messageRevisionRef.current++;
          setMessages((current) => mergeMessages(current, forwarded));
        }
        setChoosingDestination(false);
        setSelectingMessages(false);
        setSelectedMessageIds([]);
        setDrawerOpen(false);
      }
    } catch (error) {
      if (
        mountedRef.current &&
        isSelected(selectedRef, sourceKey ?? '') &&
        selectionVersionRef.current === sourceVersion
      )
        setActionError(t(errorKey(error)));
    } finally {
      forwardingRef.current = false;
      if (mountedRef.current) setForwarding(false);
    }
  };

  /** Slides the page out to the right (the chat slides back in) and then unmounts it. */
  const requestClose = () => {
    if (screenClosingRef.current) return;
    screenClosingRef.current = true;
    setScreenClosing(true);
    setScreenDragging(false);
    setScreenDrag(screenRef.current?.clientWidth || window.innerWidth);
    screenCloseTimerRef.current = window.setTimeout(
      () => onCloseRef.current(),
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 180,
    );
  };

  const pointerDown = (
    event: ReactPointerEvent,
    kind: 'screen' | 'drawer' | 'bubble',
    id?: string,
  ) => {
    if (
      event.button !== 0 ||
      (event.pointerType !== 'touch' && kind !== 'bubble') ||
      (event.target as HTMLElement).closest('input, textarea') ||
      (kind !== 'drawer' && (event.target as HTMLElement).closest('button')) ||
      (kind === 'drawer' && forwardingRef.current) ||
      (event.target as HTMLElement).closest('[data-no-swipe], [contenteditable="true"], a') ||
      window.getSelection()?.toString() ||
      modelPickerOpen ||
      confirmDeleteForEveryoneId ||
      screenClosingRef.current
    )
      return;
    cancelGesture();
    const element = event.currentTarget as HTMLElement;
    const drawer = drawerRef.current;
    const width = drawer?.offsetWidth || drawerWidth;
    gestureRef.current = {
      x: event.clientX,
      y: event.clientY,
      kind,
      id,
      pointerId: event.pointerId,
      element,
      tracking: false,
      drawerOffset: surfaceRef.current
        ? surfaceDrawerOffset(
            window.getComputedStyle(surfaceRef.current).transform,
            width,
            drawerOpen,
          )
        : drawerOpen
          ? 0
          : width,
      screenOffset: screenRef.current
        ? drawerOffsetFromTransform(
            window.getComputedStyle(screenRef.current).transform,
            screenRef.current.clientWidth || window.innerWidth,
            true,
          )
        : 0,
    };
    if (kind === 'bubble') element.setPointerCapture(event.pointerId);
    if (kind === 'bubble' && id) {
      messageMenuReturnRef.current = event.currentTarget as HTMLElement;
      pressRef.current = window.setTimeout(() => {
        if (!window.getSelection()?.toString()) setMenuId(id);
        cancelGesture();
      }, 550);
    }
  };
  const pointerMove = (event: ReactPointerEvent) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    if (window.getSelection()?.toString()) {
      cancelGesture();
      return;
    }
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      if (pressRef.current !== null) window.clearTimeout(pressRef.current);
      pressRef.current = null;
    }
    if (!gesture.tracking && Math.abs(dy) > 10 && Math.abs(dy) >= Math.abs(dx)) {
      cancelGesture();
      return;
    }
    if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
      gesture.tracking = true;
      if (!gesture.element.hasPointerCapture(event.pointerId))
        gesture.element.setPointerCapture(event.pointerId);
    }
    if (!gesture.tracking || event.pointerType !== 'touch') return;
    const logicalDx = dx * (dir === 'rtl' ? -1 : 1);
    if (gesture.kind === 'screen' && !selectedRef.current && dx < 0) return;
    if (gesture.kind === 'screen' && dx > 0) {
      setDrawerDrag(null);
      setScreenDragging(true);
      setScreenDrag(
        Math.max(
          0,
          Math.min(screenRef.current?.clientWidth || window.innerWidth, gesture.screenOffset + dx),
        ),
      );
    } else if (gesture.kind === 'screen' || gesture.kind === 'drawer') {
      if (gesture.kind === 'screen') {
        setScreenDragging(false);
        setScreenDrag((current) => (current === null ? null : 0));
      }
      setDrawerDrag(
        Math.max(
          0,
          Math.min(drawerRef.current?.offsetWidth || drawerWidth, gesture.drawerOffset + dx),
        ),
      );
    }
    if (gesture.kind === 'bubble' && logicalDx < -20 && Math.abs(dx) > Math.abs(dy)) {
      gesture.element.style.transform = `translateX(${Math.max(logicalDx, -72) * (dir === 'rtl' ? -1 : 1)}px)`;
    }
  };
  const pointerUp = (event: ReactPointerEvent) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    const swipe = event.pointerType === 'touch' ? completedSwipe(dx, dy, dir, gesture.kind) : null;
    if (gesture.kind === 'screen' && swipe === 'drawer' && selectedRef.current) setDrawerOpen(true);
    if (gesture.kind === 'screen' && swipe === 'close' && selectedRef.current) {
      setScreenDragging(false);
      setScreenDrag(null);
      backToList();
    } else if (gesture.kind === 'screen' && swipe === 'close') {
      requestClose();
    }
    if (gesture.kind === 'drawer' && swipe === 'close' && !forwardingRef.current) {
      setDrawerOpen(false);
      setChoosingDestination(false);
      setGroupForm(false);
    }
    if (gesture.kind === 'bubble' && gesture.id && swipe === 'reply') {
      setReplyId(gesture.id);
      composerRef.current?.focus();
    }
    cancelGesture();
  };

  // The personal chats are a full-screen page (--direct-chat-panel-width: 100vw).
  const panelWidth = window.innerWidth;
  const revealed =
    openingDrag !== undefined
      ? Math.min(1, Math.max(0, openingDrag / panelWidth))
      : screenDrag === null
        ? 1
        : Math.max(0, 1 - screenDrag / panelWidth);

  const onRevealRef = useRef(onReveal);
  onRevealRef.current = onReveal;
  const revealInstant =
    (screenDragging && !screenClosing) || (openingDrag !== undefined && !settling);
  useLayoutEffect(() => {
    onRevealRef.current?.(revealed, revealInstant);
  }, [revealed, revealInstant]);

  return createPortal(
    <>
      <div
        className="direct-chat-overlay"
        aria-hidden="true"
        style={{
          opacity: revealed,
          transition: screenDragging || openingDrag !== undefined ? 'none' : undefined,
          pointerEvents: openingDrag !== undefined ? 'none' : undefined,
        }}
        onClick={requestClose}
      />
      <div
        ref={(element) => {
          screenRef.current = element;
          dialogRef.current = element;
        }}
        className={`direct-chat-screen${startWithDrawer || swipeOpened ? ' from-drawer' : ''}${openingDrag !== undefined ? ' is-opening' : ''}${settling ? ' is-settling' : ''}`}
        role="dialog"
        aria-modal={openingDrag === undefined ? true : undefined}
        aria-hidden={openingDrag !== undefined}
        aria-labelledby="direct-chat-title"
        inert={screenClosing || openingDrag !== undefined}
        style={
          openingDrag !== undefined
            ? {
                animation: 'none',
                pointerEvents: 'none',
                transform: `translateX(${Math.max(0, panelWidth - openingDrag)}px)`,
              }
            : screenDrag === null
              ? undefined
              : {
                  transform: `translateX(${screenDrag}px)`,
                  animation: 'none',
                  transition:
                    screenDragging ||
                    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
                      ? 'none'
                      : 'transform 180ms ease',
                }
        }
        onPointerDown={(event) => {
          if (!menuId && !sheetOpen && openingDrag === undefined)
            pointerDown(event, drawerOpen ? 'drawer' : 'screen');
        }}
        onPointerMove={(event) => {
          if (openingDrag === undefined && gestureRef.current?.kind !== 'bubble')
            pointerMove(event);
        }}
        onPointerUp={(event) => {
          if (openingDrag === undefined && gestureRef.current?.kind !== 'bubble') pointerUp(event);
        }}
        onPointerCancel={cancelGesture}
        onLostPointerCapture={(event) => {
          if (event.target === event.currentTarget) cancelGesture();
        }}
      >
        <div
          ref={surfaceRef}
          className={`direct-chat-surface${drawerOpen ? ' is-shifted' : ''}${trackedDrawerOffset !== null ? ' is-dragging' : ''}`}
          style={
            trackedDrawerOffset !== null
              ? {
                  transform: `translateX(${trackedDrawerOffset - drawerWidth}px)`,
                  transition: 'none',
                }
              : undefined
          }
        >
          <header className="direct-chat-header" inert={drawerOpen || !!menuId || sheetOpen}>
            <div className="direct-chat-heading">
              <h1 id="direct-chat-title">{t('directChat.title')}</h1>
              <span>{t('directChat.yourId', { id: publicId })}</span>
            </div>
            {(pushState === 'on' || pushState === 'off' || pushState === 'denied') && (
              <button
                type="button"
                className={`icon-btn direct-chat-push${pushState === 'on' ? ' is-on' : ''}`}
                onClick={() => void togglePush()}
                disabled={pushState === 'denied'}
                aria-pressed={pushState === 'on'}
                aria-label={
                  pushState === 'on'
                    ? t('directChat.pushDisable')
                    : pushState === 'denied'
                      ? t('directChat.pushDenied')
                      : t('directChat.pushEnable')
                }
              >
                <Icon name="bell" size={20} />
              </button>
            )}
            <button
              type="button"
              className="icon-btn direct-chat-close"
              onClick={requestClose}
              aria-label={t('common.close')}
            >
              <Icon name="close" size={22} />
            </button>
          </header>

          {actionError && !drawerOpen && openingDrag === undefined && (
            <p className="direct-chat-action-error" role="alert">
              {actionError}
            </p>
          )}
          <div className="direct-chat-conversation" inert={drawerOpen || !!menuId || sheetOpen}>
            {loadingFriends ? (
              <p className="direct-chat-empty" role="status">
                {t('common.loading')}
              </p>
            ) : friendLoadError ? (
              <div className="direct-chat-empty">
                <p role="alert">{friendLoadError}</p>
                <button type="button" className="btn btn-ghost" onClick={() => void refreshChats()}>
                  {t('common.retry')}
                </button>
              </div>
            ) : !selectedFriend && !selectedGroup ? (
              renderChatList({
                t: t as never,
                locale,
                drafts,
                entries: listEntries,
                strip,
                archivedCount,
                showArchived,
                search,
                searchResults,
                searching: search.trim().length >= 2,
                entryTitle: (result) =>
                  chatEntries.find((entry) => entry.key === `${result.kind}:${result.targetId}`)
                    ?.title ?? '',
                loading: loadingFriends,
                onSearch: setSearch,
                onOpen: openFromList,
                onActions: (key) => setChatMenuKey(key),
                onSwipePin: (key) =>
                  void updateChatPref(key, {
                    pinned: !chatEntries.find((item) => item.key === key)?.pinnedAt,
                  }),
                onSwipeArchive: (key) =>
                  void updateChatPref(key, {
                    archived: !chatEntries.find((item) => item.key === key)?.archived,
                  }),
                onSwipeDelete: (key) => setDeleteChatKey(key),
                onRowPointerDown: startRowPress,
                onRowPointerMove: moveRowPress,
                onRowPointerEnd: endRowPress,
                onToggleArchived: () => setShowArchived((value) => !value),
                onAdd: () => {
                  setActionError(null);
                  setAddSheet(true);
                },
                onOpenResult: (result) => {
                  setSearch('');
                  selectChat(`${result.kind}:${result.targetId}`);
                },
              })
            ) : (
              <>
                <div className="direct-chat-conversation-title">
                  <div className="direct-chat-conversation-name">
                    {selectedGroup?.title ?? (selectedFriend && labelOf(selectedFriend))}
                    {selectedGroup && (
                      <small>
                        {presence.typing
                          ? t('directChat.typing')
                          : t('directChat.memberCount', { count: selectedGroup.memberIds.length })}
                      </small>
                    )}
                    {selectedFriend && (presence.typing || presence.online) && (
                      <small className={presence.typing ? 'is-typing' : 'is-online'}>
                        {presence.typing ? t('directChat.typing') : t('directChat.online')}
                      </small>
                    )}
                  </div>
                  <button
                    type="button"
                    className="icon-btn direct-chat-back"
                    onClick={backToList}
                    aria-label={t('directChat.backToChats')}
                  >
                    <Icon name="back" size={22} />
                  </button>
                </div>
                {pinnedMessages.length > 0 && (
                  <button type="button" className="direct-chat-pinned-bar" onClick={showPinned}>
                    <span aria-hidden="true">📌</span>
                    <span className="direct-chat-pinned-text">
                      <small>
                        {pinnedMessages.length > 1
                          ? `${t('directChat.pinnedMessage')} · ${pinnedMessages.length}`
                          : t('directChat.pinnedMessage')}
                      </small>
                      {pinnedMessages[0]!.content}
                    </span>
                  </button>
                )}
                {selectedEntry?.blocked && (
                  <div className="direct-chat-blocked-banner" role="status">
                    <span>{t('directChat.blockedBanner')}</span>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={chatBusy}
                      onClick={() => void toggleChatBlock(selectedEntry.key)}
                    >
                      {t('directChat.unblock')}
                    </button>
                  </div>
                )}
                {selectingMessages && (
                  <div
                    className="direct-chat-selection-toolbar"
                    role="group"
                    aria-label={t('directChat.share')}
                  >
                    <span role="status">
                      {t('directChat.selectedCount', { count: forwardIds.length })}
                    </span>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!forwardIds.length || forwarding}
                      onClick={() => {
                        setChoosingDestination(true);
                        setDrawerOpen(true);
                        setActionError(null);
                      }}
                    >
                      <Icon name="send" size={18} />
                      {t('directChat.forward')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={forwarding}
                      onClick={() => {
                        setSelectingMessages(false);
                        setSelectedMessageIds([]);
                      }}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                )}
                <div
                  className="direct-chat-messages"
                  ref={logRef}
                  role="log"
                  aria-label={t('directChat.messages')}
                  aria-live="polite"
                  aria-relevant="additions"
                  onScroll={() => {
                    cancelGesture();
                    const log = logRef.current;
                    if (log && log.scrollTop < 80 && hasOlder && !loadingOlder)
                      void loadOlderMessages();
                    if (log && selected)
                      scrollRef.current = {
                        key: selected,
                        top: log.scrollTop,
                        bottom: log.scrollHeight - log.scrollTop - log.clientHeight < 12,
                      };
                  }}
                >
                  {hasOlder && (
                    <button
                      type="button"
                      className="btn btn-ghost direct-chat-load-older"
                      disabled={loadingOlder}
                      onClick={() => void loadOlderMessages()}
                    >
                      {loadingOlder ? t('common.loading') : t('directChat.loadOlder')}
                    </button>
                  )}
                  {messageLoadError && (
                    <div className="direct-chat-message-error" role="alert">
                      <span>{messageLoadError}</span>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => {
                          const request = refreshMessagesRef.current;
                          if (
                            !selected ||
                            !isSelected(selectedRef, selected) ||
                            request?.key !== selected
                          )
                            return;
                          setLoadingMessages(true);
                          setMessageLoadError(null);
                          void request.run();
                        }}
                      >
                        {t('common.retry')}
                      </button>
                    </div>
                  )}
                  {(loadingMessages || messageScope !== selected) &&
                  visibleMessages.length === 0 ? (
                    <p className="direct-chat-empty" role="status">
                      {t('common.loading')}
                    </p>
                  ) : visibleMessages.length === 0 ? (
                    <p className="direct-chat-empty">{t('directChat.noMessages')}</p>
                  ) : (
                    <>
                      {groupDirectMessageBatches(visibleMessages).map((batch) => {
                        const items = batch.map((base) => {
                          const message = base as ChatMessage;
                          const isAiMessage = isAiDirectMessage(message);
                          const mine = message.senderId === userId;
                          const sender =
                            memberUsers.find((user) => user.id === message.senderId) ??
                            friends.find((user) => user.id === message.senderId);
                          const original =
                            message.replyTo ??
                            visibleMessages.find((item) => item.id === message.replyToMessageId);
                          const receipt = message.receipts?.find(
                            (entry) => entry.userId === selectedFriend?.id,
                          );
                          const groupReceipt = selectedGroup
                            ? receiptSummary(
                                message,
                                selectedGroup.memberIds.filter((id) => id !== userId),
                              )
                            : null;
                          return (
                            <Fragment key={message.id}>
                              {message.id === unreadFromId && (
                                <div className="direct-chat-unread-divider" role="separator">
                                  <span>{t('directChat.unreadDivider')}</span>
                                </div>
                              )}
                              <article
                                data-message-id={message.id}
                                className={`direct-chat-message${mine && !isAiMessage ? ' is-mine' : ''}${isAiMessage ? ' is-ai' : ''}${selectingMessages && selectedMessageIds.includes(message.id) ? ' is-selected' : ''}`}
                                tabIndex={0}
                                aria-label={`${selectedGroup ? (mine && !isAiMessage ? t('common.you') : sender ? labelOf(sender) : message.senderId) + ': ' : ''}${message.content}`}
                                onContextMenu={(event) => {
                                  event.preventDefault();
                                  cancelGesture();
                                  if (selectingMessages) return;
                                  messageMenuReturnRef.current = event.currentTarget;
                                  setMenuId(message.id);
                                }}
                                onKeyDown={(event) => {
                                  if (event.target !== event.currentTarget) return;
                                  if (
                                    event.key === 'Enter' ||
                                    event.key === ' ' ||
                                    event.key === 'ContextMenu' ||
                                    (event.shiftKey && event.key === 'F10')
                                  ) {
                                    event.preventDefault();
                                    cancelGesture();
                                    if (selectingMessages) {
                                      toggleMessageSelection(message.id);
                                      return;
                                    }
                                    messageMenuReturnRef.current = event.currentTarget;
                                    setMenuId(message.id);
                                  }
                                }}
                                onPointerDown={(event) => {
                                  event.stopPropagation();
                                  if (!selectingMessages) pointerDown(event, 'bubble', message.id);
                                }}
                                onPointerMove={pointerMove}
                                onPointerUp={pointerUp}
                                onPointerCancel={(event) => {
                                  event.currentTarget.style.transform = '';
                                  cancelGesture();
                                }}
                                onLostPointerCapture={(event) => {
                                  if (event.target === event.currentTarget) cancelGesture();
                                }}
                                onClick={(event) => {
                                  if (
                                    !selectingMessages ||
                                    (event.target as HTMLElement).closest('input')
                                  )
                                    return;
                                  toggleMessageSelection(message.id);
                                }}
                              >
                                {selectingMessages && (
                                  <input
                                    type="checkbox"
                                    className="direct-chat-message-select"
                                    aria-label={t('directChat.selectMessage')}
                                    checked={selectedMessageIds.includes(message.id)}
                                    disabled={
                                      !selectedMessageIds.includes(message.id) &&
                                      selectedMessageIds.length >= 100
                                    }
                                    onChange={() => toggleMessageSelection(message.id)}
                                  />
                                )}
                                {isAiMessage ? (
                                  <strong className="direct-chat-sender direct-chat-sender-ai">
                                    {message.ai?.modelName || message.forwardedFrom?.modelName
                                      ? `${t('directChat.aiAttribution')} (${message.ai?.modelName || message.forwardedFrom?.modelName})`
                                      : t('directChat.aiAttribution')}
                                  </strong>
                                ) : (
                                  selectedGroup &&
                                  !mine && (
                                    <strong className="direct-chat-sender">
                                      {sender ? labelOf(sender) : message.senderId}
                                    </strong>
                                  )
                                )}
                                {message.forwardedFrom && (
                                  <span className="direct-chat-annotation">
                                    {t('directChat.forwarded')}
                                    {!isAiMessage && message.forwardedFrom.senderName
                                      ? ` · ${message.forwardedFrom.senderName}`
                                      : ''}
                                  </span>
                                )}
                                {message.replyToMessageId && (
                                  <blockquote className="direct-chat-quote">
                                    {Boolean(
                                      original && isAiDirectMessage(original as DirectMessage),
                                    ) && (
                                      <span className="direct-chat-quote-author direct-chat-sender-ai">
                                        {(original as DirectMessage)?.ai?.modelName
                                          ? `${t('directChat.aiAttribution')} (${(original as DirectMessage).ai?.modelName}): `
                                          : `${t('directChat.aiAttribution')}: `}
                                      </span>
                                    )}
                                    {original?.content ?? t('directChat.reply')}
                                  </blockquote>
                                )}
                                {message.attachments?.length ? (
                                  <ChatAttachments api={api} attachments={message.attachments} />
                                ) : null}
                                {message.attachments?.length &&
                                /^(📷 Photo|🎤 Voice message|📎 )/.test(message.content) ? null : (
                                  <p>{message.content}</p>
                                )}
                                {message.reactions?.length ? (
                                  <div className="direct-chat-reactions" role="group">
                                    {Object.entries(
                                      message.reactions.reduce<Record<string, number>>(
                                        (counts, reaction) => ({
                                          ...counts,
                                          [reaction.emoji]: (counts[reaction.emoji] ?? 0) + 1,
                                        }),
                                        {},
                                      ),
                                    ).map(([emoji, count]) => (
                                      <button
                                        key={emoji}
                                        type="button"
                                        className={`direct-chat-reaction${
                                          message.reactions?.some(
                                            (reaction) =>
                                              reaction.userId === userId &&
                                              reaction.emoji === emoji,
                                          )
                                            ? ' is-mine'
                                            : ''
                                        }`}
                                        onPointerDown={(event) => event.stopPropagation()}
                                        onClick={() => void toggleReaction(message.id, emoji)}
                                      >
                                        {emoji}
                                        {count > 1 ? <span>{count}</span> : null}
                                      </button>
                                    ))}
                                  </div>
                                ) : null}
                                <div className="direct-chat-meta">
                                  {message.pinned ? (
                                    <span aria-label={t('directChat.pinnedMessage')}>📌</span>
                                  ) : null}
                                  <time dateTime={message.createdAt}>
                                    {new Intl.DateTimeFormat(locale, {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    }).format(new Date(message.createdAt))}
                                  </time>
                                  {mine && !isAiMessage && !selectedGroup && (
                                    <span
                                      className={`direct-chat-ticks${receipt?.readAt ? ' is-read' : ''}`}
                                      aria-label={
                                        receipt?.readAt
                                          ? t('directChat.read')
                                          : receipt?.deliveredAt
                                            ? t('directChat.delivered')
                                            : t('directChat.sent')
                                      }
                                    >
                                      {receiptMark(receipt)}
                                    </span>
                                  )}
                                  {mine && !isAiMessage && groupReceipt && (
                                    <span
                                      className={`direct-chat-ticks${groupReceipt.read ? ' is-read' : ''}`}
                                      aria-label={
                                        groupReceipt.read
                                          ? `${t('directChat.read')} ${groupReceipt.read}/${groupReceipt.total}`
                                          : groupReceipt.delivered
                                            ? `${t('directChat.delivered')} ${groupReceipt.delivered}/${groupReceipt.total}`
                                            : t('directChat.sent')
                                      }
                                    >
                                      {groupReceipt.read
                                        ? `✓✓ ${groupReceipt.read}/${groupReceipt.total}`
                                        : groupReceipt.delivered
                                          ? `✓ ${groupReceipt.delivered}/${groupReceipt.total}`
                                          : ''}
                                    </span>
                                  )}
                                </div>
                              </article>
                            </Fragment>
                          );
                        });
                        return batch[0]?.batchId ? (
                          <div
                            key={batch[0].id}
                            className={`direct-chat-message-batch${batch[0].senderId === userId ? ' is-mine' : ''}`}
                            role="group"
                            data-batch-id={batch[0].batchId}
                            aria-label={t('directChat.forwarded')}
                          >
                            {items}
                          </div>
                        ) : (
                          items
                        );
                      })}
                      {Boolean(selected && aiPendingByChat[selected]) && (
                        <article
                          className="direct-chat-message is-ai direct-chat-message-pending"
                          aria-busy="true"
                        >
                          <strong className="direct-chat-sender direct-chat-sender-ai">
                            {t('directChat.aiAttribution')}
                          </strong>
                          <p className="direct-chat-ai-typing">
                            <span className="dot" />
                            <span className="dot" />
                            <span className="dot" />
                            <span className="direct-chat-ai-generating-text">
                              {t('directChat.aiGenerating')}
                            </span>
                          </p>
                        </article>
                      )}
                    </>
                  )}
                </div>
                {reply && (
                  <div className="direct-chat-reply-preview">
                    <span>
                      {t('directChat.replyingTo', {
                        name: isAiDirectMessage(reply)
                          ? t('directChat.aiAttribution')
                          : reply.senderId === userId
                            ? t('common.you')
                            : (memberUsers.find((user) => user.id === reply.senderId)?.firstName ??
                              selectedFriend?.firstName ??
                              reply.senderId),
                      })}
                      : {reply.content}
                    </span>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => setReplyId(null)}
                      aria-label={t('common.cancel')}
                    >
                      ×
                    </button>
                  </div>
                )}
                {selected && aiErrorsByChat[selected] && (
                  <div className="direct-chat-message-error" role="alert">
                    <span>{aiErrorsByChat[selected]?.text}</span>
                    {aiErrorsByChat[selected]?.requestId && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => {
                          const requestId = aiErrorsByChat[selected]?.requestId;
                          const previous = requestId
                            ? aiRequestRef.current.get(requestId)
                            : undefined;
                          if (!previous) return;
                          const next = {
                            ...previous,
                            modelId: aiErrorsByChat[selected]?.allowModelChange
                              ? (resolvedChatModelId ?? AUTO_MODEL_CHOICE_ID)
                              : previous.modelId,
                          };
                          aiRequestRef.current.set(next.requestId, next);
                          void generateAiReply(next.requestId);
                        }}
                      >
                        {t('common.retry')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => setModelPickerOpen(true)}
                    >
                      {t('directChat.aiLongPressHint')}
                    </button>
                  </div>
                )}
                {editId && (
                  <div className="direct-chat-edit-preview">
                    <span>{t('directChat.editing')}</span>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={editing}
                      onClick={() => {
                        setEditId(null);
                        setActionError(null);
                        composerRef.current?.focus();
                      }}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                )}
                <form
                  className="direct-chat-composer"
                  onSubmit={(event) => void (editId ? saveEdit(event) : sendMessage(event))}
                >
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept={PHOTO_INPUT_ACCEPT}
                    multiple
                    hidden
                    onChange={(event) => {
                      void uploadFiles(event.target.files, 'image');
                      event.target.value = '';
                    }}
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={(event) => {
                      void uploadFiles(event.target.files, 'image');
                      event.target.value = '';
                    }}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={FILE_INPUT_ACCEPT}
                    multiple
                    hidden
                    onChange={(event) => {
                      void uploadFiles(event.target.files, 'file');
                      event.target.value = '';
                    }}
                  />
                  {!editId && (pendingFiles[selected ?? ''] ?? []).length > 0 && (
                    <div className="composer-attachments">
                      {(pendingFiles[selected ?? ''] ?? []).map((file) => (
                        <span key={file.id} className="composer-attachment-chip">
                          {file.previewUrl ? <img src={file.previewUrl} alt="" /> : null}
                          <span>{file.fileName}</span>
                          <button
                            type="button"
                            aria-label={t('directChat.removeAttachment')}
                            onClick={() =>
                              selected &&
                              setPendingFiles((current) => ({
                                ...current,
                                [selected]: (current[selected] ?? []).filter(
                                  (item) => item.id !== file.id,
                                ),
                              }))
                            }
                          >
                            <Icon name="close" size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="composer-form">
                    <textarea
                      className="composer-input"
                      ref={composerRef}
                      value={editId ? editContent : draft}
                      onChange={(event) => {
                        if (editId) setEditContent(event.target.value);
                        else if (selected) {
                          setDrafts((current) => ({ ...current, [selected]: event.target.value }));
                          const stamp = Date.now();
                          if (event.target.value && stamp - lastTypingSentRef.current > 3_000) {
                            lastTypingSentRef.current = stamp;
                            void api
                              .sendDirectChatTyping(
                                selected.startsWith('friend:') ? 'direct' : 'group',
                                selected.slice(selected.indexOf(':') + 1),
                              )
                              .catch(() => undefined);
                          }
                        }
                      }}
                      placeholder={t('directChat.messagePlaceholder')}
                      aria-label={t('directChat.messagePlaceholder')}
                      rows={1}
                      maxLength={10_000}
                      disabled={editing}
                    />
                    <div className="composer-toolbar">
                      <div className="composer-toolbar-left">
                        {!editId && (
                          <div className="composer-plus-wrap">
                            <IconButton
                              icon="plus"
                              label={t('composer.attach')}
                              disabled={attaching}
                              active={attachMenuOpen}
                              aria-expanded={attachMenuOpen}
                              onClick={() => setAttachMenuOpen((open) => !open)}
                            />
                            {attachMenuOpen && (
                              <div className="plus-popover" role="menu">
                                <button
                                  type="button"
                                  className="plus-popover-item"
                                  role="menuitem"
                                  onClick={() => {
                                    setAttachMenuOpen(false);
                                    photoInputRef.current?.click();
                                  }}
                                >
                                  <Icon name="image" size={16} /> {t('composer.attachPhotos')}
                                </button>
                                <button
                                  type="button"
                                  className="plus-popover-item"
                                  role="menuitem"
                                  onClick={() => {
                                    setAttachMenuOpen(false);
                                    cameraInputRef.current?.click();
                                  }}
                                >
                                  <Icon name="camera" size={16} /> {t('composer.attachCamera')}
                                </button>
                                <button
                                  type="button"
                                  className="plus-popover-item"
                                  role="menuitem"
                                  onClick={() => {
                                    setAttachMenuOpen(false);
                                    fileInputRef.current?.click();
                                  }}
                                >
                                  <Icon name="attach" size={16} />
                                  <span className="plus-popover-label">
                                    {t('composer.attachFiles')}
                                    <small className="plus-popover-hint">
                                      {t('composer.attachFilesHint')}
                                    </small>
                                  </span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        <button
                          ref={aiButtonRef}
                          type="button"
                          className={`direct-chat-ai-toggle icon-btn${isAiEnabled ? ' is-enabled' : ' is-disabled'}`}
                          aria-label={
                            isAiEnabled
                              ? t('directChat.aiToggleEnabled')
                              : t('directChat.aiToggleDisabled')
                          }
                          title={
                            isAiEnabled
                              ? `${t('directChat.aiToggleEnabled')} (${t('directChat.aiLongPressHint')})`
                              : `${t('directChat.aiToggleDisabled')} (${t('directChat.aiLongPressHint')})`
                          }
                          aria-pressed={isAiEnabled}
                          aria-haspopup="dialog"
                          onPointerDown={handleAiPointerDown}
                          onPointerMove={handleAiPointerMove}
                          onPointerUp={handleAiPointerUp}
                          onPointerCancel={handleAiPointerCancel}
                          onLostPointerCapture={handleAiPointerUp}
                          onContextMenu={(event) => {
                            event.preventDefault();
                            handleAiPointerCancel();
                            setModelPickerOpen(true);
                          }}
                          onClick={handleAiClick}
                          onKeyDown={handleAiKeyDown}
                        >
                          <Icon name="bot" size={18} />
                        </button>
                        {!editId && (
                          <VoiceRecorderButton
                            label={t('directChat.voiceRecord')}
                            stopLabel={t('directChat.voiceStop')}
                            disabled={attaching || sending}
                            onRecorded={(file) => void uploadFiles([file], 'file')}
                            onError={() => setActionError(t('directChat.voiceUnavailable'))}
                          />
                        )}
                      </div>
                      <IconButton
                        icon={editId ? 'check' : 'send'}
                        label={t(editId ? 'directChat.saveEdit' : 'directChat.send')}
                        type="submit"
                        disabled={
                          editId
                            ? editing || !editContent.trim()
                            : sending ||
                              attaching ||
                              (!draft.trim() && !(pendingFiles[selected ?? ''] ?? []).length)
                        }
                      />
                    </div>
                  </div>
                </form>
              </>
            )}
          </div>

          {(drawerOpen || trackedDrawerOffset !== null) && (
            <div
              className="direct-chat-drawer-backdrop"
              style={
                trackedDrawerOffset === null
                  ? undefined
                  : {
                      opacity: drawerBackdropOpacity(drawerWidth, trackedDrawerOffset),
                    }
              }
              onClick={() => {
                if (forwardingRef.current) return;
                cancelGesture();
                setDrawerOpen(false);
                setGroupForm(false);
                setChoosingDestination(false);
              }}
              aria-hidden="true"
            />
          )}
        </div>
        <aside
          ref={drawerRef}
          id="direct-chat-drawer"
          className={`direct-chat-drawer${drawerOpen ? ' is-open' : ''}${trackedDrawerOffset !== null ? ' is-dragging' : ''}`}
          aria-label={t('directChat.chats')}
          aria-hidden={!drawerOpen || openingDrag !== undefined}
          inert={!drawerOpen || openingDrag !== undefined}
          onScroll={cancelGesture}
        >
          <div className="direct-chat-drawer-header">
            <h2>{t('directChat.chats')}</h2>
            <button
              type="button"
              className="icon-btn"
              onClick={() => void refreshChats()}
              aria-label={t('common.refresh')}
              disabled={loadingFriends}
            >
              ↻
            </button>
            <button
              ref={drawerCloseRef}
              type="button"
              className="icon-btn"
              onClick={() => {
                cancelGesture();
                setDrawerOpen(false);
                setGroupForm(false);
                setChoosingDestination(false);
                drawerButtonRef.current?.focus();
              }}
              disabled={forwarding}
              aria-label={t('common.close')}
            >
              ×
            </button>
          </div>
          {!choosingDestination && (
            <form className="direct-chat-add" onSubmit={(event) => void addFriend(event)}>
              <input
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                placeholder={t('directChat.addPlaceholder')}
                aria-label={t('directChat.addPlaceholder')}
                autoComplete="off"
                maxLength={40}
              />
              <button
                className="btn btn-primary"
                type="submit"
                disabled={loadingFriends || adding || !identifier.trim()}
              >
                {adding ? t('common.loading') : t('directChat.addFriend')}
              </button>
            </form>
          )}
          <div
            className={`direct-chat-drawer-list`}
            role="group"
            aria-label={t('directChat.friends')}
          >
            <h3>{t('directChat.friends')}</h3>
            {friends.map((friend) => (
              <button
                type="button"
                key={friend.id}
                disabled={forwarding || (choosingDestination && !forwardIds.length)}
                className={`direct-chat-drawer-item${selected === `friend:${friend.id}` ? ' is-selected' : ''}`}
                onClick={() =>
                  choosingDestination
                    ? void forwardMessage({ friendId: friend.id })
                    : selectChat(`friend:${friend.id}`)
                }
                aria-current={selected === `friend:${friend.id}` ? 'true' : undefined}
              >
                {friend.photoUrl ? (
                  <img className="direct-chat-avatar" src={friend.photoUrl} alt="" />
                ) : (
                  <span className="direct-chat-avatar direct-chat-avatar-fallback">
                    {initials(friend)}
                  </span>
                )}
                {labelOf(friend)}
              </button>
            ))}
            <h3>{t('directChat.groups')}</h3>
            {groups.map((group) => (
              <button
                type="button"
                key={group.id}
                disabled={forwarding || (choosingDestination && !forwardIds.length)}
                className={`direct-chat-drawer-item${selected === `group:${group.id}` ? ' is-selected' : ''}`}
                onClick={() =>
                  choosingDestination
                    ? void forwardMessage({ groupId: group.id })
                    : selectChat(`group:${group.id}`)
                }
                aria-current={selected === `group:${group.id}` ? 'true' : undefined}
              >
                {group.title}
              </button>
            ))}
            {!choosingDestination && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setActionError(null);
                  setDrawerOpen(false);
                  setGroupForm(true);
                }}
                disabled={!friends.length}
              >
                {t('directChat.newGroup')}
              </button>
            )}
          </div>
          {choosingDestination && (
            <p className="direct-chat-drawer-hint">
              {t('directChat.chooseDestination')}{' '}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  cancelGesture();
                  setChoosingDestination(false);
                  setDrawerOpen(false);
                }}
                disabled={forwarding}
              >
                {t('common.cancel')}
              </button>
            </p>
          )}
          {actionError && (
            <p className="direct-chat-action-error" role="alert">
              {actionError}
            </p>
          )}
        </aside>

        {groupForm && (
          <div
            className="direct-chat-sheet-backdrop"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setGroupForm(false)}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-label={t('directChat.newGroup')}
            >
              {groupForm && (
                <form
                  className="direct-chat-group-form direct-chat-sheet"
                  onSubmit={(event) => void createGroup(event)}
                >
                  <label>
                    {t('directChat.groupName')}
                    <input
                      ref={groupNameRef}
                      value={groupTitle}
                      maxLength={100}
                      onChange={(event) => setGroupTitle(event.target.value)}
                      required
                    />
                  </label>
                  <p>{t('directChat.selectMembers', { count: groupMembers.length + 1 })}</p>
                  <div className="direct-chat-member-list">
                    {friends.map((friend) => (
                      <label key={friend.id}>
                        <input
                          type="checkbox"
                          checked={groupMembers.includes(friend.id)}
                          disabled={!groupMembers.includes(friend.id) && groupMembers.length >= 49}
                          onChange={(event) =>
                            setGroupMembers((current) =>
                              event.target.checked
                                ? [...current, friend.id]
                                : current.filter((id) => id !== friend.id),
                            )
                          }
                        />
                        {labelOf(friend)}
                      </label>
                    ))}
                  </div>
                  {actionError && (
                    <p className="direct-chat-action-error" role="alert">
                      {actionError}
                    </p>
                  )}
                  <div>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => setGroupForm(false)}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={creating || !groupTitle.trim() || !groupMembers.length}
                    >
                      {creating ? t('common.loading') : t('directChat.createGroup')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {addSheet && (
          <div
            className="direct-chat-sheet-backdrop"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setAddSheet(false)}
          >
            <div
              className="direct-chat-sheet"
              role="dialog"
              aria-label={t('directChat.addMenu')}
              onClick={(event) => event.stopPropagation()}
            >
              <h2>{t('directChat.newChat')}</h2>
              <form
                className="direct-chat-add"
                onSubmit={(event) => {
                  void addFriend(event);
                }}
              >
                <input
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  placeholder={t('directChat.addPlaceholder')}
                  aria-label={t('directChat.addPlaceholder')}
                  autoComplete="off"
                  maxLength={40}
                  autoFocus
                />
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={loadingFriends || adding || !identifier.trim()}
                >
                  {adding ? t('common.loading') : t('directChat.addFriend')}
                </button>
              </form>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={!friends.length}
                onClick={() => {
                  setAddSheet(false);
                  setActionError(null);
                  setGroupForm(true);
                }}
              >
                {t('directChat.newGroup')}
              </button>
              {actionError && (
                <p className="direct-chat-action-error" role="alert">
                  {actionError}
                </p>
              )}
            </div>
          </div>
        )}

        {chatMenuEntry && (
          <div
            className="direct-chat-sheet-backdrop"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={closeChatMenu}
          >
            <div
              role="menu"
              aria-label={`${t('directChat.chatActions')}: ${chatMenuEntry.title}`}
              className="direct-chat-menu"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                disabled={chatBusy}
                onClick={() =>
                  void updateChatPref(chatMenuEntry.key, { pinned: !chatMenuEntry.pinnedAt })
                }
              >
                {chatMenuEntry.pinnedAt ? t('directChat.unpin') : t('directChat.pin')}
              </button>
              {chatMenuEntry.kind === 'direct' && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setRenameValue(chatMenuEntry.alias ?? '');
                    setRenameKey(chatMenuEntry.key);
                    setChatMenuKey(null);
                  }}
                >
                  {t('directChat.rename')}
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                disabled={chatBusy}
                onClick={() =>
                  void updateChatPref(chatMenuEntry.key, { muted: !chatMenuEntry.muted })
                }
              >
                {chatMenuEntry.muted ? t('directChat.unmute') : t('directChat.mute')}
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={chatBusy}
                onClick={() =>
                  void updateChatPref(chatMenuEntry.key, { archived: !chatMenuEntry.archived })
                }
              >
                {chatMenuEntry.archived ? t('directChat.unarchive') : t('directChat.archive')}
              </button>
              {chatMenuEntry.kind === 'direct' && (
                <button
                  type="button"
                  role="menuitem"
                  className="direct-chat-menu-item--danger"
                  disabled={chatBusy}
                  onClick={() => void toggleChatBlock(chatMenuEntry.key)}
                >
                  {chatMenuEntry.blocked ? t('directChat.unblock') : t('directChat.block')}
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="direct-chat-menu-item--danger"
                onClick={() => {
                  setDeleteChatKey(chatMenuEntry.key);
                  setChatMenuKey(null);
                }}
              >
                {chatMenuEntry.kind === 'group'
                  ? t('directChat.leaveGroup')
                  : t('directChat.deleteChat')}
              </button>
              {actionError && (
                <p className="direct-chat-action-error" role="alert">
                  {actionError}
                </p>
              )}
            </div>
          </div>
        )}

        {renameEntry && (
          <div
            className="direct-chat-sheet-backdrop"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setRenameKey(null)}
          >
            <form
              className="direct-chat-sheet"
              aria-label={t('directChat.renameTitle')}
              onClick={(event) => event.stopPropagation()}
              onSubmit={(event) => {
                event.preventDefault();
                void updateChatPref(renameEntry.key, { alias: renameValue.trim() || null });
              }}
            >
              <h2>{t('directChat.renameTitle')}</h2>
              <input
                value={renameValue}
                onChange={(event) => setRenameValue(event.target.value)}
                placeholder={t('directChat.renamePlaceholder')}
                aria-label={t('directChat.renamePlaceholder')}
                maxLength={60}
                autoFocus
              />
              {actionError && (
                <p className="direct-chat-action-error" role="alert">
                  {actionError}
                </p>
              )}
              <div>
                <button type="button" className="btn btn-ghost" onClick={() => setRenameKey(null)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={chatBusy}>
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        )}

        {deleteEntry && (
          <div
            className="direct-chat-sheet-backdrop"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setDeleteChatKey(null)}
          >
            <div
              role="alertdialog"
              className="direct-chat-sheet"
              aria-label={t('directChat.deleteChat')}
              onClick={(event) => event.stopPropagation()}
            >
              <h2>{deleteEntry.title}</h2>
              <button
                type="button"
                className="btn btn-danger"
                disabled={chatBusy}
                onClick={() => void deleteChat(deleteEntry.key, 'me')}
              >
                {deleteEntry.kind === 'group'
                  ? t('directChat.leaveGroup')
                  : t('directChat.deleteForMe')}
              </button>
              {deleteEntry.kind === 'direct' && (
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={chatBusy}
                  onClick={() => void deleteChat(deleteEntry.key, 'everyone')}
                >
                  {t('directChat.deleteForBoth')}
                </button>
              )}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDeleteChatKey(null)}
              >
                {t('common.cancel')}
              </button>
              {actionError && (
                <p className="direct-chat-action-error" role="alert">
                  {actionError}
                </p>
              )}
            </div>
          </div>
        )}

        {menuId && (
          <div
            ref={menuRef}
            className="direct-chat-menu-backdrop"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => {
              setMenuId(null);
              messageMenuReturnRef.current?.focus();
            }}
          >
            <div
              role="menu"
              aria-label={t('directChat.messageActions')}
              className="direct-chat-menu"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="direct-chat-menu-reactions" role="group">
                {REACTION_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    role="menuitem"
                    aria-label={emoji}
                    onClick={() => {
                      const targetId = menuId;
                      setMenuId(null);
                      void toggleReaction(targetId, emoji);
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              <button
                type="button"
                role="menuitem"
                autoFocus
                onClick={() => {
                  setReplyId(menuId);
                  setMenuId(null);
                  composerRef.current?.focus();
                }}
              >
                {t('directChat.reply')}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setSelectedMessageIds([menuId]);
                  setSelectingMessages(true);
                  setMenuId(null);
                  setReplyId(null);
                  setEditId(null);
                  setActionError(null);
                }}
              >
                {t('directChat.share')}
              </button>
              {visibleMessages.some(
                (message) => message.id === menuId && canEditDirectMessage(message, userId),
              ) && (
                <button
                  type="button"
                  role="menuitem"
                  disabled={editing}
                  onClick={() => {
                    const message = visibleMessages.find((item) => item.id === menuId);
                    if (!message) return;
                    setEditId(message.id);
                    setEditContent(message.content);
                    setReplyId(null);
                    setMenuId(null);
                    setActionError(null);
                    window.requestAnimationFrame(() => composerRef.current?.focus());
                  }}
                >
                  {t('directChat.edit')}
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  const targetId = menuId;
                  setMenuId(null);
                  void togglePin(targetId);
                }}
              >
                {messages.find((m) => m.id === menuId)?.pinned
                  ? t('directChat.unpinMessage')
                  : t('directChat.pinMessage')}
              </button>
              <button type="button" role="menuitem" onClick={() => void deleteMessage(menuId)}>
                {t('directChat.deleteForMe')}
              </button>
              {messages.find((m) => m.id === menuId)?.senderId === userId && (
                <button
                  type="button"
                  role="menuitem"
                  className="direct-chat-menu-item--danger"
                  onClick={() => {
                    const targetId = menuId;
                    setMenuId(null);
                    setConfirmDeleteForEveryoneId(targetId);
                  }}
                >
                  {t('directChat.deleteForEveryone')}
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuId(null);
                  messageMenuReturnRef.current?.focus();
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        <ModelPicker
          open={modelPickerOpen}
          onClose={handleCloseModelPicker}
          models={internalModels}
          section="chat"
          selectedModelChoice={chatModelChoice}
          resolvedModelId={resolvedChatModelId}
          onSelect={(modelId) => {
            updateAiPreferences({ ...accountPreferences, modelChoice: modelId });
            handleCloseModelPicker();
          }}
        />

        <ConfirmDialog
          open={confirmDeleteForEveryoneId !== null}
          title={t('directChat.deleteForEveryone')}
          message={t('directChat.deleteForEveryoneConfirm')}
          confirmLabel={t('directChat.deleteForEveryone')}
          cancelLabel={t('common.cancel')}
          danger
          onConfirm={() => {
            if (confirmDeleteForEveryoneId) {
              void deleteMessage(confirmDeleteForEveryoneId, { forEveryone: true });
            }
            setConfirmDeleteForEveryoneId(null);
          }}
          onCancel={() => {
            setConfirmDeleteForEveryoneId(null);
            messageMenuReturnRef.current?.focus();
          }}
        />
      </div>
    </>,
    document.body,
  );
}
